import {useMutation, UseMutationResult, useQuery, UseQueryResult} from '@tanstack/react-query';
import {useContext} from 'react';

import {useReviewsAnalysisFilter} from '../../../page/main/reviews-analysis/reviews-analysis-hook';
import {useLocale} from '../../../provider/locale/locale-hook';
import {MainFilterContext} from '../../../provider/main-filter/main-filter';
import {MainFilterContextType} from '../../../provider/main-filter/main-filter-type';
import {useSnackbar} from '../../../provider/snackbar/snackbar-hook';
import {useTableSorter} from '../../../util/antd/table/table-sorter-hook';
import {usePagination} from '../../../util/pagination-hook/pagination-hook';
import {PaginationOptionsType} from '../../../util/pagination-hook/pagination-hook-type';

import {
    fetchReviewsAnalysisAspects,
    fetchReviewsAnalysisAspectsTone,
    fetchReviewsByAspectsDetailed,
    fetchReviewsCountByAspects,
    fetchTopAspects,
    requestAspectsReport,
} from './review-aspects-api';
import {
    AspectsAnalysisRequestParametersType,
    ExportAspectsReportOptionsType,
    ReviewAnalyticsAspectsHookType,
    ReviewAspectsToneType,
    ReviewsByAspectsDetailedHookType,
    ReviewsCountByAspectsType,
    TopAspectsItemType,
    TopAspectsQueryParametersType,
} from './review-aspects-type';

function useAspectsAnalysisRequestParameters(defaultDateRange?: [Date, Date]): AspectsAnalysisRequestParametersType {
    const {comparePeriodFilter, singleFilter} = useReviewsAnalysisFilter(false, defaultDateRange);

    const {mainFilterKey} = useContext<MainFilterContextType>(MainFilterContext);

    return {
        ...singleFilter,
        comparedStartDate: comparePeriodFilter.startDate,
        comparedEndDate: comparePeriodFilter.endDate,
        filterKey: mainFilterKey,
    };
}

export function useReviewAnalyticsAspects(paginationOption?: PaginationOptionsType): ReviewAnalyticsAspectsHookType {
    const sorter = useTableSorter();
    const {sorterQueryValue} = sorter;

    const requestOptions = useAspectsAnalysisRequestParameters();

    const pagination = usePagination({
        dependencies: null,
        shouldSaveState: false,
        ...paginationOption,
    });

    const query = useQuery(
        ['review-analysis-aspects', pagination, requestOptions, sorterQueryValue],
        () =>
            fetchReviewsAnalysisAspects({
                sortQueryValue: sorterQueryValue,
                ...requestOptions,
                ...pagination,
            }),
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
            onSuccess: (data) => {
                pagination.onDataLoaded(data);
            },
            onError: pagination.onDataLoadFailed,
        }
    );

    return {
        ...query,
        pagination,
        sorter,
    };
}

export function useReviewsByAspectsDetailed(
    search: string | null = null,
    paginationOption?: PaginationOptionsType
): ReviewsByAspectsDetailedHookType {
    const sorter = useTableSorter();
    const {sorterQueryValue} = sorter;

    const requestOptions = useAspectsAnalysisRequestParameters();

    const pagination = usePagination({
        // Reset to the first page whenever the search term changes.
        dependencies: search,
        shouldSaveState: false,
        ...paginationOption,
    });

    const query = useQuery(
        ['reviews-by-aspects-detailed', pagination, requestOptions, sorterQueryValue, search],
        () =>
            fetchReviewsByAspectsDetailed({
                sortQueryValue: sorterQueryValue,
                search,
                ...requestOptions,
                ...pagination,
            }),
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
            onSuccess: (data) => {
                pagination.onDataLoaded(data);
            },
            onError: pagination.onDataLoadFailed,
        }
    );

    return {
        ...query,
        pagination,
        sorter,
    };
}

// BST-10541: single-location page — same aspect stats scoped to one company_id.
export function useReviewAnalyticsAspectsByCompany(
    companyId: number,
    defaultDateRange?: [Date, Date],
    paginationOption?: PaginationOptionsType
): ReviewAnalyticsAspectsHookType {
    const sorter = useTableSorter();
    const {sorterQueryValue} = sorter;

    const requestOptions = {...useAspectsAnalysisRequestParameters(defaultDateRange), companyIds: [companyId]};

    const pagination = usePagination({
        dependencies: null,
        shouldSaveState: false,
        ...paginationOption,
    });

    const query = useQuery(
        ['review-analysis-aspects-by-company', companyId, pagination, requestOptions, sorterQueryValue],
        () =>
            fetchReviewsAnalysisAspects({
                sortQueryValue: sorterQueryValue,
                ...requestOptions,
                ...pagination,
            }),
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
            onSuccess: (data) => {
                pagination.onDataLoaded(data);
            },
            onError: pagination.onDataLoadFailed,
        }
    );

    return {
        ...query,
        pagination,
        sorter,
    };
}

export function useReviewsCountByAspectsByCompany(
    companyId: number,
    defaultDateRange?: [Date, Date]
): UseQueryResult<ReviewsCountByAspectsType> {
    const requestOptions = {...useAspectsAnalysisRequestParameters(defaultDateRange), companyIds: [companyId]};

    return useQuery(
        ['reviews-count-by-aspects-by-company', companyId, requestOptions],
        () => fetchReviewsCountByAspects(requestOptions),
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
            refetchOnMount: false,
        }
    );
}

export function useAspectsToneDynamics(): UseQueryResult<ReviewAspectsToneType> {
    const requestOptions = useAspectsAnalysisRequestParameters();

    return useQuery(['aspects-tone-dynamics', requestOptions], () => fetchReviewsAnalysisAspectsTone(requestOptions), {
        refetchOnWindowFocus: false,
        refetchInterval: false,
        refetchOnMount: false,
    });
}

export function useReviewsCountByAspects(): UseQueryResult<ReviewsCountByAspectsType> {
    const requestOptions = useAspectsAnalysisRequestParameters();

    return useQuery(['reviews-count-by-aspects', requestOptions], () => fetchReviewsCountByAspects(requestOptions), {
        refetchOnWindowFocus: false,
        refetchInterval: false,
        refetchOnMount: false,
    });
}

export function useTopAspects(
    filterParameters: TopAspectsQueryParametersType
): UseQueryResult<Array<TopAspectsItemType>> {
    const {mainFilterKey} = useContext<MainFilterContextType>(MainFilterContext);

    return useQuery(
        ['top-aspects', mainFilterKey, filterParameters],
        () => fetchTopAspects(mainFilterKey, filterParameters),
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
        }
    );
}

export function useRequestAspectsReport(): UseMutationResult<void, unknown, ExportAspectsReportOptionsType> {
    const {snackbar} = useSnackbar();

    const {getLocalizedString} = useLocale();

    return useMutation({
        mutationFn: (options) => {
            return requestAspectsReport(options);
        },
        onSuccess: () => {
            snackbar.success({
                message: getLocalizedString('TEXT__EXPORT__SENT_BY_EMAIL__TITLE'),
                description: getLocalizedString('TEXT__EXPORT__SENT_BY_EMAIL__DESCRIPTION'),
            });
        },
        onError: () => {
            snackbar.error({
                message: getLocalizedString('ERROR__SOMETHING_WENT_WRONG'),
                description: getLocalizedString('TEXT__EXPORT__SENT_BY_EMAIL__ERROR_DESCRIPTION'),
            });
        },
    });
}
